import React, { useState, useEffect, useRef } from 'react';
import './Styles.css';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import Hls from 'hls.js';

const HLS_STREAM_URL = 'https://cdn.itassist.one/church/stream/index.m3u8';

const ServiceSection = () => {
  const [user, setUser] = useState(null);
  const [attendanceCount, setAttendanceCount] = useState("");
  const [isAttendanceSubmitted, setIsAttendanceSubmitted] = useState(
    () => sessionStorage.getItem('attendanceSubmitted') === 'true'
  );
  const [isLive, setIsLive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isStreamOffline, setIsStreamOffline] = useState(false);
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [headingRef, headingVisible] = useScrollAnimation({ threshold: 0.2 });
  const [servicesRef, servicesVisible] = useScrollAnimation({ threshold: 0.1 });

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser && storedUser !== "undefined" && storedUser !== "null") {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error("Error parsing stored user in ServiceSection:", e);
        localStorage.removeItem("user");
      }
    }
  }, []);

  useEffect(() => {
    // Poll live status every 60 seconds so the LIVE badge updates automatically
    const checkLiveStatus = async () => {
      try {
        const ytPromise = fetch(`${process.env.REACT_APP_API_URL || 'https://ceindiazone2.in'}/api/youtube/status`)
          .then(res => res.json())
          .then(data => !!(data && data.isLive))
          .catch(() => false);

        const hlsPromise = fetch(HLS_STREAM_URL, { cache: 'no-store' })
          .then(res => res.ok)
          .catch(() => false);

        const [ytLive, hlsLive] = await Promise.all([ytPromise, hlsPromise]);
        setIsLive(ytLive || hlsLive);
      } catch (err) {
        console.error('Error checking live status:', err);
        setIsLive(false);
      }
    };

    checkLiveStatus(); // Run immediately on mount
    const liveInterval = setInterval(checkLiveStatus, 60000); // then every 60s

    return () => clearInterval(liveInterval);
  }, []);

  const showVideo = user && isAttendanceSubmitted;

  // Initialize HLS player when showVideo becomes true
  useEffect(() => {
    if (!showVideo) return;

    const videoEl = videoRef.current;
    if (!videoEl) return;

    setIsStreamOffline(false);

    const playVideo = () => {
      const playPromise = videoEl.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsMuted(videoEl.muted);
          })
          .catch(() => {
            // Autoplay blocked — fall back to muted so video frames show
            videoEl.muted = true;
            setIsMuted(true);
            videoEl.play().catch(e => console.error("Muted autoplay failed:", e));
          });
      }
    };

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });
      hls.loadSource(HLS_STREAM_URL);
      hls.attachMedia(videoEl);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsStreamOffline(false);
        playVideo();
      });
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.warn("HLS stream offline or unavailable:", data);
          setIsStreamOffline(true);

          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              hls.destroy();
              break;
          }
        }
      });
      hlsRef.current = hls;
    } else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support
      videoEl.src = HLS_STREAM_URL;
      videoEl.addEventListener('loadedmetadata', () => {
        setIsStreamOffline(false);
        playVideo();
      });
      videoEl.addEventListener('error', () => {
        setIsStreamOffline(true);
      });
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [showVideo]);

  const handleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.requestFullscreen) {
      video.requestFullscreen().catch(err => console.log('Fullscreen request denied:', err));
    } else if (video.webkitEnterFullscreen) {
      video.webkitEnterFullscreen(); // iOS Safari native fullscreen
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen();
    } else if (video.msRequestFullscreen) {
      video.msRequestFullscreen();
    }
  };

  const determineCurrentService = () => {
    const now = new Date();
    const day = now.getDay(); // 0 is Sunday, 3 is Wednesday
    const hours = now.getHours() + now.getMinutes() / 60;

    if (day === 0) {
      if (hours >= 5.0 && hours < 9.5) return "Sunday Hindi Service (6am – 8am)";
      if (hours >= 9.5 && hours < 14.5) return "Sunday English Service (10am – 1pm)";
      if (hours >= 14.5 && hours < 21.0) return "Sunday Marathi Service (5pm – 7pm)";
      return "Sunday Service";
    } else if (day === 3) {
      if (hours >= 12.0 && hours < 15.5) return "Wednesday Lunch Hour Service (1pm – 2pm)";
      if (hours >= 18.0 && hours <= 23.0) return "Wednesday Evening Service (7pm – 9pm)";
      return "Wednesday Mid-Week Service";
    }
    return "Special Service";
  };

  const handleAttendanceSubmit = async (e) => {
    e.preventDefault();
    if (attendanceCount && parseInt(attendanceCount) > 0) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'https://ceindiazone2.in'}/api/attendance/submit`,
          {
            method: "POST",
            headers: { 
              "Content-Type": "application/json",
              "Authorization": `Bearer ${user.currentSessionToken}`
            },
            body: JSON.stringify({ 
              userId: user.id, 
              count: parseInt(attendanceCount),
              serviceName: determineCurrentService()
            }),
          }
        );
        if (response.ok) {
          setIsAttendanceSubmitted(true);
          sessionStorage.setItem('attendanceSubmitted', 'true');
        } else if (response.status === 401 || response.status === 403) {
          alert("Session expired or logged in on another device. Please sign in again.");
          localStorage.removeItem("user");
          window.location.href = "/signin";
        } else {
          alert("Something went wrong submitting attendance. Please try again.");
        }
      } catch {
        alert("Network error. Please check your connection.");
      }
    } else {
      alert("Please enter a valid number of attendees.");
    }
  };

  return (
    <div id="service" className="service-section">
      <div className="container">

        {/* HEADER — img2 pattern: eyebrow LEFT, title RIGHT */}
        <div
          ref={headingRef}
          className={`section-header fade-in-up ${headingVisible ? 'visible' : ''}`}
        >
          <div className="sh-label">
            <span className="eyebrow">Services</span>
          </div>
          <div className="sh-title">
            <h2 className="section-title">
              TOGETHER, WE TAKE GOD'S PRESENCE TO THE NATIONS
            </h2>
          </div>
        </div>

        {/* Content */}
        <div
          ref={servicesRef}
          className={`service-wrapper stagger-children ${servicesVisible ? 'visible' : ''}`}
        >
          <div className="service-day">
            <h4 className="day-title">SUN</h4>
            <div className="service-box">
              <p><strong>Hindi Service</strong>6am – 8am</p>
              <p><strong>English Service</strong>10am – 1pm</p>
              <p><strong>Marathi Service</strong>5pm – 7pm</p>
            </div>
          </div>

          <div className="service-center">
            <div className="video-wrapper">
              {!showVideo && (
                <div
                  className={`video-overlay ${!user ? 'clickable-overlay no-user' : ''}`}
                  onClick={!user ? () => {
                    sessionStorage.setItem('loginRedirect', '/#service');
                    navigate('/signIn');
                  } : undefined}
                >
                  <div className="overlay-content">
                    {!user ? (
                      <div className="play-button-container">
                        <svg viewBox="0 0 24 24" className="play-icon" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    ) : (
                      <div className="attendance-form" onClick={(e) => e.stopPropagation()}>
                        <h3>Welcome, {user.name}!</h3>
                        <p>How many people are watching with you?</p>
                        <form onSubmit={handleAttendanceSubmit}>
                          <input
                            type="number"
                            min="1"
                            placeholder="e.g. 1"
                            value={attendanceCount}
                            onChange={(e) => setAttendanceCount(e.target.value)}
                            required
                          />
                          <button type="submit">Start Watching</button>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {showVideo ? (
                <div className="video-inner-container">
                  {isStreamOffline && (
                    <div className="stream-offline-overlay" style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'rgba(10, 15, 29, 0.92)',
                      zIndex: 30,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '24px',
                      textAlign: 'center',
                      color: '#fff',
                      borderRadius: '8px'
                    }}>
                      <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '12px',
                        fontSize: '22px'
                      }}>
                        📡
                      </div>
                      <h4 style={{ margin: '0 0 6px 0', fontSize: '15px', fontWeight: '700', color: '#f3f4f6' }}>
                        Live Service Stream Standby
                      </h4>
                      <p style={{ margin: '0 0 16px 0', fontSize: '12.5px', color: '#9ca3af', maxWidth: '320px', lineHeight: '1.4' }}>
                        The stream is currently offline. Tap below when service begins to check connection.
                      </p>
                      <button
                        onClick={() => {
                          setIsStreamOffline(false);
                          if (hlsRef.current) {
                            hlsRef.current.destroy();
                            hlsRef.current = null;
                          }
                          setIsAttendanceSubmitted(false);
                          sessionStorage.removeItem('attendanceSubmitted');
                          setTimeout(() => {
                            setIsAttendanceSubmitted(true);
                            sessionStorage.setItem('attendanceSubmitted', 'true');
                          }, 100);
                        }}
                        style={{
                          padding: '8px 18px',
                          backgroundColor: 'var(--gold, #d29d2b)',
                          color: '#000',
                          border: 'none',
                          borderRadius: '20px',
                          cursor: 'pointer',
                          fontWeight: '700',
                          fontSize: '12px',
                          boxShadow: '0 4px 14px rgba(0,0,0,0.3)'
                        }}
                      >
                        🔄 Check Stream
                      </button>
                    </div>
                  )}
                  {isMuted && !isStreamOffline && (
                    <button
                      onClick={() => {
                        if (videoRef.current) {
                          videoRef.current.muted = false;
                          setIsMuted(false);
                        }
                      }}
                      style={{
                        position: 'absolute',
                        top: '12px',
                        left: '12px',
                        zIndex: 25,
                        backgroundColor: 'rgba(220, 38, 38, 0.9)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '20px',
                        padding: '6px 14px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      🔊 Click to Unmute
                    </button>
                  )}
                  {!isStreamOffline && (
                    <button
                      onClick={handleFullscreen}
                      title="View Fullscreen / Landscape"
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        zIndex: 25,
                        backgroundColor: 'rgba(15, 23, 42, 0.85)',
                        color: '#fff',
                        border: '1px solid rgba(255, 255, 255, 0.25)',
                        borderRadius: '20px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}
                    >
                      ⛶ Fullscreen
                    </button>
                  )}
                  <video
                    ref={videoRef}
                    controls
                    playsInline
                    muted={isMuted}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#000',
                      objectFit: 'contain',
                      display: 'block',
                      zIndex: 10
                    }}
                  />
                </div>
              ) : (
                <img
                  src="https://images.unsplash.com/photo-1510563800743-aed236490d08?w=1200&auto=format&fit=crop"
                  alt="Service Thumbnail"
                  className="video-thumbnail"
                />
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="live-now-container">
              <span className={`live-now-badge ${isLive ? 'is-live' : 'is-offline'}`}>
                <span className={`live-dot ${isLive ? 'is-live' : 'is-offline'}`}></span>
                {isLive ? 'LIVE NOW' : 'OFFLINE'}
              </span>
            </div>
            <div className="service-day" style={{ marginTop: '0' }}>
              <h4 className="day-title">WED</h4>
              <div className="service-box">
                <p><strong>English Service</strong>1 pm – 2 pm</p>
                <p><strong>Hindi/English Service</strong>7pm – 9pm</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceSection;
